import { jwtStore } from "../auth/jwtStore";
import { authzStore } from "../authz/authzStore";

/**
 * Single chokepoint for all calls to the bulk-export jobs API
 * (CloudFront `/jobs/*` → API Gateway → API Lambda).
 *
 * Mirrors the auth contract enforced by `api/patientRegistry.ts`:
 * - Every call sets `Authorization: Bearer <jwt>` from `jwtStore`.
 * - Calls without a JWT throw `AuthRequiredError` and pop the paste modal.
 * - 401 responses clear the cached JWT and re-pop the modal.
 *
 * The jobs API itself does NOT require a `ProjectId` request header —
 * the projectId is part of the submission body, and the API Lambda
 * checks ownership against the JWT's `userId` claim. So this wrapper
 * is intentionally narrower than the patient-registry one.
 */

export class JobsAuthRequiredError extends Error {
    constructor(reason: "no-jwt" | "rejected") {
        super(reason === "no-jwt" ? "no jwt cached" : "jwt rejected by jobs API");
        this.name = "JobsAuthRequiredError";
    }
}

export class JobsApiError extends Error {
    public readonly status: number;
    public readonly body: string;
    constructor(status: number, body: string) {
        super(`jobs API request failed with status ${status}: ${body}`);
        this.name = "JobsApiError";
        this.status = status;
        this.body = body;
    }
}

let openModalCallback: () => void = () => {};

export function setJobsOpenModalCallback(fn: () => void): void {
    openModalCallback = fn;
}

export interface JobsFetchOptions extends Omit<RequestInit, "headers" | "body"> {
    headers?: Record<string, string>;
    body?: unknown;
}

async function jobsFetch(path: string, init: JobsFetchOptions = {}): Promise<Response> {
    const jwt = jwtStore.get();
    if (!jwt) {
        openModalCallback();
        throw new JobsAuthRequiredError("no-jwt");
    }
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const headers: Record<string, string> = {
        Accept: "application/json",
        ...(init.headers ?? {}),
        Authorization: `Bearer ${jwt}`,
    };
    let body: BodyInit | undefined;
    if (init.body !== undefined) {
        headers["Content-Type"] = headers["Content-Type"] ?? "application/json";
        body = typeof init.body === "string" ? init.body : JSON.stringify(init.body);
    }
    const response = await fetch(normalizedPath, { ...init, headers, body });
    if (response.status === 401) {
        jwtStore.clear();
        openModalCallback();
        throw new JobsAuthRequiredError("rejected");
    }
    // PRD-005 panel security review (trinity I-1): only the locked
    // "forbidden" discriminator from our jobs-api edge means "Subject_Read
    // missing → deny page." Any other 403 (currently none from the jobs
    // API surface, but future routes could add them) is treated as a
    // per-call error, not a global deny.
    if (response.status === 403) {
        const clone = response.clone();
        try {
            const body = (await clone.json()) as { error?: unknown };
            if (body && body.error === "forbidden") {
                authzStore.setDenied();
            }
        } catch {
            // Body not JSON; treat as a per-call error, no global deny.
        }
    }
    return response;
}

async function parsedFetch<T>(path: string, init: JobsFetchOptions = {}): Promise<T> {
    const response = await jobsFetch(path, init);
    if (!response.ok) {
        const text = await response.text().catch(() => "(unreadable body)");
        throw new JobsApiError(response.status, text);
    }
    return (await response.json()) as T;
}

export type JobStatus =
    | "queued"
    | "gathering"
    | "awaiting_confirmation"
    | "running"
    | "completed"
    | "failed"
    | "cancelled"
    | "cancelling"
    | "expired";

export interface Job {
    jobId: string;
    userId: string;
    organizationId: string;
    projectId: string;
    checklistId: string;
    /** Human-readable checklist name. Stored on the job row at submit time. */
    checklistName?: string | null;
    organizationName?: string | null;
    projectName?: string | null;
    jobName: string;
    /**
     * Legacy inline subject UUID array. Present on rows submitted before
     * PRD-001 ADR `prd-001-architecture-subjects-storage.md` moved the
     * list to S3. New rows omit this field and carry `subjectCount`
     * instead. Readers MUST prefer `subjectCount` when both are available.
     */
    subjectIds?: string[];
    /**
     * Authoritative subject count. Set at submit time on every new job
     * (PRD-001 ADR `prd-001-architecture-subjects-storage.md`). Older job
     * rows may omit this field; readers fall back to `subjectIds.length`.
     * Closes issue #11 — the SPA was reading `subjectIds.length` only
     * and rendering 0 for every new job after the storage rework.
     */
    subjectCount?: number;
    status: JobStatus;
    submittedAt: string;
    startedAt?: string;
    /** Set by the worker when the job reaches a terminal state
     * (succeeded / failed / cancelled). Used by JobsBadge to compute
     * "new since last viewed" counts. */
    completedAt?: string;
    /** Set by the expirer Lambda when the row's status flips to "expired". */
    expiredAt?: string;
    cursor?: number;
    resultS3Key?: string;
    failuresS3Key?: string;
    /** PRD-001 rev 14: the results-NDJSON artifact ("Results (JSON)" button).
     * Absent on jobs finalized before rev 14 — the button is attribute-driven
     * and simply doesn't render for them. */
    resultsJsonS3Key?: string;
    /** PRD-001 §Job-history: succeeded + failed counts. For terminal
     *  jobs these are authoritative (finalize writes them). For running
     *  jobs the API annotates them live from the ResultsTable so the
     *  history page can advance counts + the progress percentage. Absent
     *  for queued jobs. */
    succeededCount?: number;
    failedCount?: number;
    /** ISO timestamp at which the result files become eligible for
     * lifecycle deletion (~7 days after completion). Sent to the SPA so
     * the history table can show the user when their files vanish. */
    resultExpiresAt?: string;
    /** PRD-001 rev 13 — filter-mode jobs only. The persisted filter
     * parameters (user-authored search terms, no PHI). */
    filterCreatedStart?: string | null;
    filterLabel?: string | null;
    /** Matched-so-far count while `gathering`; the final matched count once
     * gathering completes / while `awaiting_confirmation`. */
    gatheredCount?: number;
    /** The registry's page-1 totalCount when the live envelope provided one —
     * the progress denominator during gathering (best-effort). */
    gatherTotalCount?: number;
    /** Set when the job entered awaiting_confirmation. Feeds the nav badge
     * ("user must come look") alongside completedAt. */
    awaitingConfirmationAt?: string;
    /** ISO deadline after which a never-confirmed job auto-cancels (7 days). */
    confirmationExpiresAt?: string;
    /** Present on jobs that terminated before any evaluation ran
     * (`gathering` | `awaiting_confirmation`) — no CSV artifacts exist and
     * succeeded/failed counts will never arrive. */
    terminalPhase?: string;
}

/** PRD-001 rev 13: the two mutually-exclusive submission modes. */
export interface SubmitJobBase {
    checklistId: string;
    projectId: string;
    jobName?: string;
    organizationName?: string;
    projectName?: string;
    checklistName?: string;
}

export interface SubmitExplicitRequest extends SubmitJobBase {
    subjectIds: string[];
    filter?: never;
}

export interface SubmitFilterRequest extends SubmitJobBase {
    /** ≥1 constraint required; createdStart is the resolved RFC 3339 UTC
     * instant; label rides verbatim. The backend re-validates both. */
    filter: { createdStart?: string; label?: string };
    subjectIds?: never;
}

export type SubmitJobRequest = SubmitExplicitRequest | SubmitFilterRequest;

export interface SubmitJobResponse {
    jobId: string;
    status: JobStatus;
    jobName: string;
}

export interface DownloadUrlResponse {
    url: string;
    expiresIn: number;
}

export const jobsApi = {
    list: (): Promise<{ jobs: Job[] }> => parsedFetch("/jobs"),

    get: (jobId: string): Promise<Job> => parsedFetch(`/jobs/${encodeURIComponent(jobId)}`),

    submit: (req: SubmitJobRequest): Promise<SubmitJobResponse> => parsedFetch("/jobs", { method: "POST", body: req }),

    cancel: (jobId: string): Promise<{ jobId: string; status: JobStatus }> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/cancel`, { method: "POST" }),

    /** PRD-001 rev 13 §Large-cohort gate: Continue on an awaiting-confirmation
     * job. 409 = the job moved on (double-click, cancel, or auto-cancel won). */
    confirm: (jobId: string): Promise<{ jobId: string; status: JobStatus; failureReason?: string }> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/confirm`, { method: "POST" }),

    rerun: (jobId: string): Promise<SubmitJobResponse> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/rerun`, { method: "POST" }),

    resultUrl: (jobId: string): Promise<DownloadUrlResponse> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/result-url`),

    /** PRD-001 rev 14: presigned URL for the results NDJSON. */
    resultJsonUrl: (jobId: string): Promise<DownloadUrlResponse> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/result-json-url`),

    failuresUrl: (jobId: string): Promise<DownloadUrlResponse> =>
        parsedFetch(`/jobs/${encodeURIComponent(jobId)}/failures-url`),
};

/** Test-only export for unit tests that need to assert raw fetch behavior. */
export const __testing = { jobsFetch, parsedFetch };
